import React, { useEffect, useState } from "react";
import { Form, Button, Message } from "semantic-ui-react";
import "./AvailabilityForm.css";
import { useFormik } from "formik";
import Panel from "./Panel";
import { set } from "lodash";

const POST_CODE_REGEX = /^([a-zA-Z]{1,2}\d[a-zA-Z\d]?) ?(\d[a-zA-Z]{2})$/;
const PHONE_NUMBER_REGEX = /^0([1-6]\d{8,10})$/;

export const PHONE_NUMBER_ERROR_MESSAGE =
  "Please enter a valid UK landline number";
export const POST_CODE_ERROR_MESSAGE = "Please enter a valid UK Post Code";
export const ADDRESS_SEARCH_NO_RESULTS =
  "We were unable to find any addresses for this post code please check it and try again.";
export const AVAILABLITY_CHECK_NO_RESULTS =
  "No available products for the selected address";
export const SERVER_ERROR_MESSAGE = "A server error occured, please try again.";

const validate = ({ phoneNumber, postCode, address }) => {
  const errors = {};

  if (!phoneNumber && !address) {
    errors.address = true;
  }

  if (phoneNumber && !phoneNumber.match(PHONE_NUMBER_REGEX)) {
    errors.phoneNumber = true;
  }

  if (postCode && !postCode.match(POST_CODE_REGEX)) {
    errors.postCode = true;
  }

  return errors;
};

export default function AvailabilityForm({ availablityCheck, addressSearch }) {
  const [errorBannerMessage, setErrorBannerMessage] = useState(null);
  const [hasAvailabilityRun, setAvailabilityRun] = useState(false);

  useEffect(() => {
    if (availablityCheck.loading || addressSearch.loading) {
      setErrorBannerMessage(null);
      return;
    }

    const availabilityError = availablityCheck.error?.message;
    const addressError = addressSearch.error?.message;

    const noResults =
      hasAvailabilityRun &&
      (!availablityCheck?.data ||
        availablityCheck?.data?.broadbandGroups?.length === 0);

    if (addressError === "No Results") {
      setErrorBannerMessage({
        header: "No Results",
        content: ADDRESS_SEARCH_NO_RESULTS,
      });
    } else if (availabilityError === "No Results" || noResults) {
      setErrorBannerMessage({
        header: "No Results",
        content: AVAILABLITY_CHECK_NO_RESULTS,
      });
    } else if (
      availabilityError === "Server Error" ||
      addressError === "Server Error"
    ) {
      setErrorBannerMessage({
        header: "Server Error",
        content: SERVER_ERROR_MESSAGE,
      });
    } else {
      setErrorBannerMessage(null);
    }
  }, [
    availablityCheck.loading,
    availablityCheck.data,
    availablityCheck.error,
    addressSearch.loading,
    addressSearch.error,
    hasAvailabilityRun,
  ]);

  const { values, errors, touched, ...formik } = useFormik({
    validate,
    validateOnChange: true,
    validateOnMount: true,
    initialValues: {
      phoneNumber: "",
      postCode: "",
      address: "",
    },
  });

  useEffect(() => {
    if (addressSearch.data?.length > 0) {
      formik.setFieldValue("address", "");
    }
  }, [addressSearch.data]);

  const selectedAddressObj = values.address && addressSearch.data
    ? addressSearch.data.find(item =>
        item.id === values.address || item.addressReference?.uprn === values.address
      )
    : null;

 function onAvailabilityFormSubmit() {
  setAvailabilityRun(false);

  if (!errors.address && !errors.phoneNumber) {
    let selectedAddress = undefined;
    
    // Only look for selected address if we have address search data and an address is selected
    if (addressSearch.data && values.address) {
      selectedAddress = addressSearch.data?.find((x) => {
        const qualifier = x.addressReference?.qualifier?.toLowerCase();
        if (qualifier === "silver") {
          return x.addressReference?.uprn === values.address;
        } else if (qualifier === "gold") {
          return x.id === values.address;
        }
        return false;
      });

      if (!selectedAddress) {
        console.error("Selected address not found in addressSearch.data", values.address);
        return;
      }
    }

    const phoneNumber = values.phoneNumber;

    sessionStorage.setItem(
      "customerDetails",
      JSON.stringify({ phoneNumber, selectedAddress })
    );

    availablityCheck.makeRequest(phoneNumber, selectedAddress);
    setAvailabilityRun(true);
  }
}


  function onPostCodeFormSubmit() {
    setAvailabilityRun(false);

    if (values.postCode && !errors.postCode) {
      const [_, area, street] = values.postCode.match(POST_CODE_REGEX);
      formik.setFieldTouched("postCode", true);
      formik.setFieldValue("address", "");
      availablityCheck.reset();
      addressSearch.makeRequest(`${area} ${street}`);
    }
  }

  function showOrHideError(e) {
    const inputName = e.nativeEvent.target.name;

    if (touched[inputName]) {
      formik.setFieldTouched(inputName, false);
    }

    if (e.keyCode === 13) {
      formik.setFieldTouched(inputName, true);
    }
  }

  return (
    <>
      <Panel header="Enter details to check availability">
        <p className="availability__help-text">
          Enter a UK landline number or search for an address below to perform
          an availability check. Phone numbers will provide the most accurate
          results.
        </p>

        <Form onSubmit={onAvailabilityFormSubmit} autoComplete="off">
          <Form.Group>
            <Form.Input
              inline
              className={
                touched.phoneNumber && errors.phoneNumber ? "error" : ""
              }
              label="Phone Number"
              name="phoneNumber"
              data-testid="phoneNumber"
              disabled={availablityCheck.loading}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              onKeyDown={showOrHideError}
            />
          </Form.Group>
          {touched.phoneNumber && errors.phoneNumber && (
            <span className="availability__error">
              {PHONE_NUMBER_ERROR_MESSAGE}
            </span>
          )}
        </Form>

        <Form onSubmit={onPostCodeFormSubmit} autoComplete="off">
          <Form.Group>
            <Form.Input
              inline
              className={touched.postCode && errors.postCode ? "error" : ""}
              label="Post Code"
              disabled={availablityCheck.loading || addressSearch.loading}
              onKeyDown={showOrHideError}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              name="postCode"
              data-testid="postCode"
            />
            <Button
              className="button-primary"
              content="Find Address"
              loading={addressSearch.loading}
              disabled={
                availablityCheck.loading || !values.postCode || errors.postCode
              }
            />
          </Form.Group>
          {touched.postCode && errors.postCode && (
            <span className="availability__error">
              {POST_CODE_ERROR_MESSAGE}
            </span>
          )}
        </Form>

        <Form onSubmit={onAvailabilityFormSubmit}>
          {addressSearch.data && (
            <select
              disabled={availablityCheck.loading}
              name="address"
              value={values.address}
              onChange={formik.handleChange}
              data-testid="address"
              style={{
                width: "100%",
                maxHeight: "200px",
                overflowY: "auto",
                fontSize: "14px",
                lineHeight: "1.4",
                border: "1px solid #ccc",
                borderRadius: "4px",
                padding: "4px",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
              }}
            >
              <option value="" disabled>
                Please select an address
              </option>
              {addressSearch.data.map((item) => {
                const { id, address, addressReference } = item;
                const qualifier =
                  addressReference?.qualifier?.toLowerCase() || "";
                const uprn = addressReference?.uprn || "";
                const backgroundColor =
                  qualifier.toLowerCase() === "gold"
                    ? "#fff7d1"
                    : qualifier.toLowerCase() === "silver"
                    ? "#f5f5f5"
                    : "white";
                const textColor = "#333";

                return (
                  <option
                    key={id}
                    value={qualifier.toLowerCase() === "silver" ? uprn : id}
                    style={{
                      backgroundColor,
                      color: textColor,
                      padding: "5px",
                      borderBottom: "1px solid #ddd",
                      fontWeight:
                        qualifier.toLowerCase() === "gold" ||
                        qualifier.toLowerCase() === "silver"
                          ? "bold"
                          : "normal",
                    }}
                  >
                    {address} {qualifier ? ` - ${qualifier}` : ""}
                  </option>
                );
              })}
            </select>
          )}
        {selectedAddressObj?.addressClassification && (
          <p className="availability__classification" data-testid="address-classification">
            Classification: {[
              selectedAddressObj.addressClassification.classificationCode,
              selectedAddressObj.addressClassification.classificationDescription,
              selectedAddressObj.addressClassification.premiseType,
            ].filter(Boolean).join(' · ')}
          </p>
        )}
        </Form>

        {errorBannerMessage && <Message error {...errorBannerMessage} />}
      </Panel>

      <Button
        className="button-primary"
        content="Check Availability"
        loading={availablityCheck.loading}
        disabled={errors.address || errors.phoneNumber}
        onClick={onAvailabilityFormSubmit}
        floated="right"
      />
    </>
  );
}
